import React, { useCallback, useReducer } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import userUtils from 'utils/User';
import Authentication from 'Authentication';
import LoveActions from 'actions/LoveActions';
import skuUtils from 'utils/Sku';
import skuHelpers from 'utils/skuHelpers';
import { HEADER_VALUE } from 'constants/authentication';
import { getImageAltText } from 'utils/Accessibility';
import helpersUtils from 'utils/Helpers';

const { deferTaskExecution } = helpersUtils;

const anonymous = userUtils.isAnonymous;

const initialState = { hover: false, isPending: false, pendingSku: {} };

function loveReducer(state, action) {
    switch (action.type) {
        case 'SET_HOVER':
            return { ...state, hover: action.value };
        case 'SET_PENDING_SKU':
            return { ...state, isPending: action.isPending, pendingSku: { skuId: action.skuId, status: action.status } };
        default:
            return state;
    }
}

function buildHandleOnClick(sku, currentProduct, analyticsContext, reduxDispatch) {
    return (e, skuLoveData) => {
        e.preventDefault();
        deferTaskExecution(() => {
            const isAnon = anonymous();
            const analyticsData = { context: analyticsContext, nextPageContext: analyticsContext };
            const imageProps = {
                id: sku.skuId,
                skuImages: sku.skuImages,
                disableLazyLoad: true,
                altText: getImageAltText(sku)
            };
            Authentication.requireAuthentication(null, null, analyticsData, null, false, HEADER_VALUE.USER_CLICK)
                .then(() => {
                    if (!isAnon || !skuHelpers.isSkuLoved(sku.skuId)) {
                        reduxDispatch(LoveActions.showMyListsModal(true));
                        reduxDispatch(LoveActions.setSkuLoveData(skuLoveData));
                        reduxDispatch(LoveActions.setSkuLoveImageData(imageProps));
                        reduxDispatch(LoveActions.setCurrentProduct({ ...sku, ...currentProduct }));
                    }
                })
                .catch(() => {});
        });
    };
}

function useLoveInteractions({ sku, currentProduct, analyticsContext }) {
    const [{ hover, isPending, pendingSku }, localDispatch] = useReducer(loveReducer, initialState);
    const reduxDispatch = useDispatch();
    useSelector(state => state.loves.shoppingListIds, shallowEqual);

    const isActive = useCallback(skuId => (skuId === pendingSku.skuId ? pendingSku.status === 'loved' : skuHelpers.isSkuLoved(skuId)), [pendingSku]);

    const handleOnClick = useCallback(buildHandleOnClick(sku, currentProduct, analyticsContext, reduxDispatch), [
        sku,
        currentProduct,
        analyticsContext,
        reduxDispatch
    ]);

    const mouseEnter = useCallback(e => {
        if (!Sephora.isTouch) {
            e.stopPropagation();
            localDispatch({ type: 'SET_HOVER', value: true });
        }
    }, []);

    const mouseLeave = useCallback(e => {
        if (!Sephora.isTouch) {
            e.stopPropagation();
            localDispatch({ type: 'SET_HOVER', value: false });
        }
    }, []);

    return {
        hover,
        isPending,
        isActive,
        handleOnClick,
        mouseEnter,
        mouseLeave
    };
}

const withLoveInteractions = WrappedComponent => {
    const LoveInteractions = ({
        currentProduct = {}, sku = {}, loveSource, conversionType, analyticsContext, addPadding
    }) => {
        const {
            hover, isPending, isActive, handleOnClick, mouseEnter, mouseLeave
        } = useLoveInteractions({ sku, currentProduct, analyticsContext });
        const isCustomSetsProduct = skuUtils.isCustomSetsSingleSkuProduct(currentProduct) || skuUtils.isCustomSetsGroupedSkuProduct(currentProduct);

        return (
            <WrappedComponent
                skuLoveData={{
                    loveSource,
                    skuId: sku.skuId,
                    productId: currentProduct.productDetails.productId,
                    isOnlyFewLeft: sku.isOnlyFewLeft
                }}
                isActive={isActive(sku.skuId)}
                isCustomSetsProduct={isCustomSetsProduct}
                handleOnClick={isPending ? () => {} : handleOnClick}
                mouseEnter={mouseEnter}
                mouseLeave={mouseLeave}
                hover={hover}
                conversionType={conversionType}
                addPadding={addPadding}
            />
        );
    };

    LoveInteractions.displayName = `LoveInteractions(${WrappedComponent.displayName})`;

    return LoveInteractions;
};

export default withLoveInteractions;
